import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserProfileService } from '../services/userProfile.service';

export default function ProfileSidebar() {
  const { user, userProfile, profileLoading } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showYearDropdown, setShowYearDropdown] = useState(false);

  const monthNames = [
    "1月", "2月", "3月", "4月", "5月", "6月",
    "7月", "8月", "9月", "10月", "11月", "12月"
  ];

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDay = firstDay.getDay();
    const daysInMonth = getDaysInMonth(year, month);
    const days = [];

    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`blank-${i}`} className="gray"></div>);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const isToday =
        i === new Date().getDate() &&
        month === new Date().getMonth() &&
        year === new Date().getFullYear();

      days.push(
        <div key={i} className={isToday ? "today" : ""}>
          {i}
        </div>
      );
    }

    return days;
  };

  const handlePrev = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNext = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAvatarUrl = async () => {
      if (userProfile?.avatar_path && user) {
        try {
          const avatarUrl = await UserProfileService.getAvatarUrl();
          if (avatarUrl) {
            setAvatarUrl(avatarUrl);
          }
        } catch (error) {
          console.error('Error fetching avatar URL:', error);
        }
      }
    };

    fetchAvatarUrl();
  }, [userProfile?.avatar_path, user]);

  const getDisplayName = () => {
    if (profileLoading) return "読み込み中...";
    if (userProfile?.name) return userProfile.name;
    if (user?.email) return user.email.split('@')[0];
    return "ユーザー";
  };

  return (
    <aside className="right-sidebar">
      <div className="profile-header">
        <h2>プロフィール</h2>
        <button className="edit-button" onClick={() => navigate('/setting')}>
          <svg className="edit-svgIcon" viewBox="0 0 512 512">
            <path d="M410.3 231l11.3-11.3-33.9-33.9-62.1-62.1L291.7 89.8l-11.3 11.3-22.6 22.6L58.6 322.9c-10.4 10.4-18 23.3-22.2 37.4L1 480.7c-2.5 8.4-.2 17.5 6.1 23.7s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L387.7 253.7 410.3 231zM160 399.4l-9.1 22.7c-4 3.1-8.5 5.4-13.3 6.9L59.4 452l23-78.1c1.4-4.9 3.8-9.4 6.9-13.3l22.7-9.1v32c0 8.8 7.2 16 16 16h32zM362.7 18.7L348.3 33.2 325.7 55.8 314.3 67.1l33.9 33.9 62.1 62.1 33.9 33.9 11.3-11.3 22.6-22.6 14.5-14.5c25-25 25-65.5 0-90.5L453.3 18.7c-25-25-65.5-25-90.5 0zm-47.4 168l-144 144c-6.2 6.2-16.4 6.2-22.6 0s-6.2-16.4 0-22.6l144-144c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6z"></path>
          </svg>
        </button>
      </div>

      <div className="profile-info">
        <div className="profile-image">
          {avatarUrl && (
            <img
              src={avatarUrl}
              alt="プロフィール画像"
              className="avatar-img"   // ← クラス追加
            />
          )}
          {/* {avatarUrl && <img src={avatarUrl} alt="プロフィール画像" />} */}
          <div className="profile-ring">
            <svg className="ring-svg" viewBox="0 0 120 120">
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#42a5f5" />
                  <stop offset="100%" stopColor="#1565c0" />
                </linearGradient>
              </defs>
              <circle
                className="ring-segment"
                cx="60"
                cy="60"
                r="50"
                stroke="url(#gradient)"
                strokeWidth="5"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="26.18 10"
              />
            </svg>
          </div>
        </div>
        <p className="name">
          {getDisplayName()}
        </p>
      </div>

      <div className="calendar-header">
        <span className="arrow prev" onClick={handlePrev}>&lt;</span>
        <span
          className="month"
          id="month-label"
          onClick={() => setShowYearDropdown(!showYearDropdown)}
          style={{ cursor: 'pointer' }}
        >
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </span>
        <span className="arrow next" onClick={handleNext}>&gt;</span>
      </div>

      {showYearDropdown && (
        <div className="year-scroll">
          {[...Array(100)].map((_, i) => {
            const year = new Date().getFullYear() - 50 + i;
            return (
              <div
                key={year}
                className={`year-option ${year === currentDate.getFullYear() ? 'selected' : ''}`}
                onClick={() => {
                  setCurrentDate(new Date(year, currentDate.getMonth(), 1));
                  setShowYearDropdown(false);
                }}
              >
                {year}
              </div>
            );
          })}
        </div>
      )}

      <div className="calendar-days">
        <div>日</div><div>月</div><div>火</div><div>水</div><div>木</div><div>金</div><div>土</div>
      </div>

      <div className="calendar-dates" id="calendar-dates">
        {renderCalendar()}
      </div>
    </aside>
  );
}
